/**
 * The Documents tab — label and formatting helpers (VALE-404).
 *
 * Nothing here restates a value the codebase already owns: the size and date
 * formatters, the file glyph and the three core-document labels are re-exported
 * from `components/documents/v2`; the per-type name comes from `FILE_TYPES`,
 * the registry the upload dialog's own type picker reads; and "how long ago"
 * comes from `formatRelativeTime`. The only thing genuinely defined here is the
 * doc-state mapping.
 */

import {
  formatDate,
  formatFileSize,
  getCoreDocumentTypeLabel,
  getFileIcon,
  getFileIconColor
} from '@/components/documents/v2';
import type { DocumentItem } from '@/components/documents/v2';
import { FILE_TYPES, FileTypeSlug } from '@/lib/services/files/file-types';
import { formatRelativeTime } from '@/lib/utils/date-format';

export {
  formatDate,
  formatFileSize,
  getCoreDocumentTypeLabel,
  getFileIcon,
  getFileIconColor
};

/**
 * A document's live state, as the row reads it.
 *
 * `analysing` and `stalled` are not two flavours of the same thing: `analysing`
 * maps onto the backend's in-progress `File.status` rungs, whereas `stalled` is
 * purely client-side — the poll-window timeout `DocumentsTabContent` stamps onto
 * an entry it has given up waiting for (LF-4971). The list has to tell them
 * apart because only one of them is recoverable by trying again.
 */
export type DocState = 'ready' | 'analysing' | 'stalled';

export function docStateOf(doc: DocumentItem): DocState {
  if (doc.status === 'failed') return 'stalled';
  if (doc.status === 'uploaded' || doc.status === 'processing') {
    return 'analysing';
  }
  return 'ready';
}

/**
 * The document's type in a solicitor's words — "Signed form", "Primary ID" —
 * read off `FILE_TYPES`, the same registry that names the options in the upload
 * dialog's type picker. A slug the registry does not know (or an absent one, on
 * an optimistic row that has not been fetched back yet) yields nothing rather
 * than a guess, and the row simply omits the segment.
 *
 * `additional-document` is deliberately unlabelled: it is the catch-all every
 * uncategorised file lands in, so printing "Additional document" on most rows of
 * a section already headed "Additional documents" is noise, not information.
 */
export function typeLabel(typeSlug: string | undefined): string | undefined {
  if (!typeSlug || typeSlug === FileTypeSlug.ADDITIONAL_DOCUMENT) {
    return undefined;
  }
  return FILE_TYPES[typeSlug as FileTypeSlug]?.name;
}

/**
 * "4 minutes ago" / "2 days ago" / "12 Aug 2026".
 *
 * **The phrasing is `formatRelativeTime`'s, not this file's** — that helper owns
 * "how long ago" for the whole app, and a second implementation here would drift
 * from it silently. This composes rather than restates: past a week it hands off
 * to Documents' own `formatDate` instead, because a row saying "43 days ago"
 * makes the reader do arithmetic, and `formatRelativeTime`'s own fallback
 * formatter renders "August 3, 2026" where every other date on this tab reads
 * "3 Aug 2026".
 *
 * So the only decision left here is *which* formatter takes over and when. The
 * week boundary mirrors `formatRelativeTime`'s own; if that ever moves, this
 * simply hands off a little early or late and the helper's fallback covers the
 * gap — a wording difference on old rows, never a wrong date.
 */
const A_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';

  if (Date.now() - then >= A_WEEK_MS) return formatDate(iso);
  return formatRelativeTime(iso);
}
